import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Paginator } from "@orange-marmalade/paginate-this";
import { INCOMING_LINE_ITEMS } from "MODULES/IncomingQuotes/constants";
import { NoteTooltip } from "MODULES/shared/components/NoteTooltip";
import {
  DataTable,
  IconReceived,
  PurchaseOrderLineItemStatusCell,
  PartNumber
} from "MODULES/shared/components";
import { dollars } from "LIB/formatters/currency";
import { AsIsFlag } from "MODULES/IncomingQuotes/shared/AsIsFlag";
import { alphaMonthYear } from "LIB/formatters/date";
import { ExchangeIcon } from "MODULES/PurchaseOrders/shared/ExchangeIcon";
import LineItemQtyCell from "MODULES/shared/components/LineItemQtyCell/LineItemQtyCell";
import { displayCost } from "MODULES/PurchaseOrders/shared/helpers";
import { displayLink } from "MODULES/PurchaseOrders/helpers";
import LineItemControls from "./LineItemControls";
import EditPart from "./EditPart/EditPart";

const paginatorList = "purchaseOrderLineItems";

const ReceivedIcon = ({ quantity_received: quantityReceived, quantity }) => {
  if (quantityReceived === quantity) {
    return <IconReceived />;
  }
  return null;
};

ReceivedIcon.propTypes = {
  quantity_received: PropTypes.number,
  quantity: PropTypes.number
};

export default class LineItemsTable extends Component {
  static propTypes = {
    results: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
    isLoading: PropTypes.bool.isRequired
  };

  state = {
    addPartModalOpen: false,
    editPartModalOpen: false
  };

  openEditPartModal = lineItem =>
    this.setState({
      editPartModalOpen: true,
      selectedLineItem: lineItem
    });

  closeEditPartModal = () => this.setState({ editPartModalOpen: false });

  renderRow = (r, idx) => {
    const classes = classNames("clickable", {
      "text-muted": r.cancelled
    });

    return (
      <tr
        key={r.id}
        className={classes}
        onClick={() => this.openEditPartModal(r)}
      >
        <td>
          <div style={{ whiteSpace: "nowrap" }}>
            <div style={{ textAlign: "right" }}>
              <LineItemQtyCell lineItem={r} showFull />
              {idx + 1}
            </div>
          </div>
        </td>
        <PurchaseOrderLineItemStatusCell lineItem={r}>
          <div style={{ display: "flex", alignItems: "center" }}>
            {r.has_vendor_rma && (
              <NoteTooltip
                icon="fa-shield"
                placement="top"
                note="Vendor RMA"
                color="white"
                style={{ marginRight: "5px" }}
              />
            )}
            <PartNumber part={r.part} statsTooltip />
          </div>
        </PurchaseOrderLineItemStatusCell>
        <td>{displayLink(r)}</td>
        <td>
          {r.alternate &&
            (r.alternate.alternate_part_id ? (
              <PartNumber
                part={{
                  id: r.alternate.alternate_part_id,
                  part_number: r.alternate.name
                }}
              />
            ) : (
              r.alternate.name
            ))}
        </td>
        <td>
          {r.trace_type && INCOMING_LINE_ITEMS.traceTypes[r.trace_type].label}
        </td>
        <td>{r.traced_to}</td>
        <td>{r.buyer && r.buyer.full_name}</td>
        <td>{r.co_buyer && r.co_buyer.full_name}</td>
        <td>{r.third_buyer && r.third_buyer.full_name}</td>
        <td>{alphaMonthYear(r.tag_date)}</td>
        <td>{r.tagged_by}</td>
        <td className="text-center">{r.lead_time}</td>
        <td>
          {r.condition && INCOMING_LINE_ITEMS.condition[r.condition].label}
          <AsIsFlag lineItem={r} className="m-l-5" />
        </td>
        <td className="text-center">
          <ExchangeIcon lineItem={r} />
        </td>
        <td className="text-right">{r.quantity}</td>
        <td className="text-right">
          <ReceivedIcon {...r} /> {r.quantity_received}
        </td>
        <td className="text-right">
          {displayCost(r) && dollars(displayCost(r))}
        </td>
        <td className="text-right">
          {r.purchase_for_exchange && dollars(r.cost)}
        </td>
        <td className="text-center td-shrink-icon">
          {(r.internal_note || r.external_note) && (
            <NoteTooltip
              note={[r.internal_note, r.external_note]
                .filter(n => n !== null && n !== "")
                .join(" | ")}
              className={classNames({ "link-muted": r.cancelled })}
            />
          )}
        </td>
        <td className="text-center td-shrink-icon">
          <LineItemControls lineItem={r} />
        </td>
      </tr>
    );
  };

  render() {
    const { results, isLoading } = this.props;
    const { editPartModalOpen, selectedLineItem } = this.state;

    return (
      <div>
        <EditPart
          open={editPartModalOpen}
          close={this.closeEditPartModal}
          selectedLineItem={selectedLineItem}
        />
        <DataTable.Mini loading={isLoading} numbered noMargin>
          <DataTable.Head>
            <th />
            <th>Part #</th>
            <th>Serial #</th>
            <th>Buy As Alt</th>
            <th>Exp Trace Type</th>
            <th>Exp Trace To</th>
            <th>Buyer</th>
            <th>Co-Buyer</th>
            <th>Third Buyer</th>
            <th>Tag Date</th>
            <th>Tagged By</th>
            <th className="text-center">Lead Time (Days)</th>
            <th>Condition</th>
            <th>Exch</th>
            <th className="text-right">Qty Ord.</th>
            <th className="text-right">Qty Rec.</th>
            <th className="text-right">Exch Fee/Cost Ea</th>
            <th className="text-right">Outright Value</th>
            <th className="text-center" />
            <th className="text-center" />
          </DataTable.Head>
          <DataTable.Body>{results.map(this.renderRow)}</DataTable.Body>
        </DataTable.Mini>
        <nav
          className="text-center margin-top-none"
          aria-label="Search results pages"
        >
          <Paginator listId={paginatorList} />
        </nav>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/modules/PurchaseOrders/components/Detail/LineItemsTable/LineItemsTable.jsx